import { ElementType } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ArrowLeft, MessageCircleMore, RefreshCw, Search } from 'lucide-react';
import {
  InstagramConversation,
  InstagramMessage,
} from '../../../utils/types';

export const igAccent = '#C13584';
export const igAccentSoft = '#FCE7F3';
export const igBorder = 'rgba(15, 23, 42, 0.08)';
export const igShellBg = '#F7F4F8';
export const igThreadBg =
  'radial-gradient(circle at top left, rgba(255,255,255,0.92), rgba(247,244,248,0.96) 42%, rgba(241,237,244,1) 100%)';

export const formatInstagramTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatInstagramConversationTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  if (sameDay) return formatInstagramTime(value);

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (sameYesterday) return 'Yesterday';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

export const formatInstagramDay = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const sameDay =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  if (sameDay) return 'Today';

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (sameYesterday) return 'Yesterday';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: today.getFullYear() === date.getFullYear() ? undefined : 'numeric',
  }).format(date);
};

export const getInstagramMessageStatus = (message: Partial<InstagramMessage>) =>
  (message.status || '').toString().toLowerCase();

export const getInstagramMessageAttachments = (
  message: Partial<InstagramMessage>,
) => {
  const attachments = message.attachments;
  if (Array.isArray(attachments)) {
    return attachments;
  }

  return [];
};

export const InstagramInboxShell = ({
  sidebar,
  thread,
  hideSidebarOnMobile = false,
}: {
  sidebar: React.ReactNode;
  thread: React.ReactNode;
  hideSidebarOnMobile?: boolean;
}) => (
  <Box bg={igShellBg} minH="100vh" px={{ base: 0, xl: 5 }} py={{ base: 0, xl: 5 }}>
    <Box
      maxW="1600px"
      mx="auto"
      h={{ base: '100vh', xl: 'calc(100vh - 40px)' }}
      bg="white"
      borderWidth={{ base: '0', xl: '1px' }}
      borderColor={{ xl: igBorder }}
      borderRadius={{ xl: '28px' }}
      overflow="hidden"
      boxShadow={{ xl: '0 24px 64px rgba(15, 23, 42, 0.08)' }}
    >
      <Flex h="100%">
        <Box
          w={{ base: '100%', md: '380px', xl: '410px' }}
          borderRight="1px solid"
          borderColor={igBorder}
          bg="white"
          h="100%"
          overflow="hidden"
          display={{ base: hideSidebarOnMobile ? 'none' : 'block', md: 'block' }}
          flexShrink={0}
        >
          {sidebar}
        </Box>
        <Box flex="1" h="100%" bg={igThreadBg}>
          {thread}
        </Box>
      </Flex>
    </Box>
  </Box>
);

export const InstagramSidebarHeader = ({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) => (
  <Flex
    px={5}
    pt={5}
    pb={4}
    align="center"
    justify="space-between"
    borderBottom="1px solid"
    borderColor={igBorder}
  >
    <VStack align="flex-start" spacing={0.5}>
      <Text fontWeight="800" fontSize="xl" color="gray.900">
        {title}
      </Text>
      {subtitle ? (
        <Text fontSize="sm" color="gray.500">
          {subtitle}
        </Text>
      ) : null}
    </VStack>
    <HStack spacing={2}>{actions}</HStack>
  </Flex>
);

export const InstagramSearchBar = ({
  value,
  onChange,
  placeholder = 'Search conversations',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <Box px={4} py={3} borderBottom="1px solid" borderColor={igBorder}>
    <InputGroup>
      <InputLeftElement pointerEvents="none">
        <Icon as={Search as ElementType} color="gray.400" boxSize={4} />
      </InputLeftElement>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        bg="#F7F4F8"
        borderRadius="999px"
        borderColor="transparent"
        _focus={{
          borderColor: '#F3B2D1',
          boxShadow: '0 0 0 1px #F3B2D1',
          bg: 'white',
        }}
      />
    </InputGroup>
  </Box>
);

export const InstagramSidebarEmpty = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <VStack px={8} py={12} spacing={3} align="center" textAlign="center">
    <Box
      boxSize="60px"
      borderRadius="20px"
      bg={igAccentSoft}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={igAccent}
    >
      <Icon as={MessageCircleMore as ElementType} boxSize={6} />
    </Box>
    <Text fontWeight="700" color="gray.800">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.500" maxW="280px" lineHeight="1.7">
      {description}
    </Text>
  </VStack>
);

export const InstagramSidebarSkeleton = () => (
  <Stack spacing={0}>
    {Array.from({ length: 8 }).map((_, index) => (
      <HStack key={index} spacing={3} px={4} py={3}>
        <Skeleton boxSize="44px" borderRadius="full" />
        <Box flex={1}>
          <Skeleton h="12px" w="42%" mb={2} />
          <Skeleton h="11px" w="76%" />
        </Box>
      </HStack>
    ))}
  </Stack>
);

export const InstagramConversationRow = ({
  conversation,
  active = false,
  href,
  onClick,
  showUnreadDot = false,
}: {
  conversation: InstagramConversation;
  active?: boolean;
  href?: string;
  onClick?: () => void;
  showUnreadDot?: boolean;
}) => {
  const preview =
    conversation.last_message?.text ||
    conversation.snippet ||
    'No messages yet';
  const timestamp =
    conversation.last_message?.timestamp ||
    conversation.last_message_at ||
    conversation.updated_time ||
    conversation.created_at;

  return (
    <Box
      as={href ? 'a' : 'div'}
      {...(href ? { href } : {})}
      onClick={onClick}
      display="block"
      px={4}
      py={3.5}
      textDecoration="none"
      bg={active ? '#FDF2F8' : 'transparent'}
      borderLeft="3px solid"
      borderLeftColor={active ? igAccent : 'transparent'}
      _hover={{ bg: active ? '#FDF2F8' : '#FAF7FB' }}
      transition="background 0.18s ease"
    >
      <HStack align="flex-start" spacing={3}>
        <Avatar
          size="md"
          name={conversation.participant_username || 'Instagram user'}
          src={conversation.profile_picture || undefined}
          bg={igAccent}
          color="white"
          flexShrink={0}
        />
        <Box flex="1" minW={0}>
          <Flex justify="space-between" align="center" mb={1}>
            <Text fontWeight="700" color="gray.900" noOfLines={1}>
              {conversation.participant_username || 'Instagram user'}
            </Text>
            <HStack spacing={2} flexShrink={0} ml={2}>
              {showUnreadDot ? (
                <Box boxSize="8px" borderRadius="full" bg={igAccent} />
              ) : null}
              <Text fontSize="xs" color="gray.500">
                {formatInstagramConversationTime(timestamp)}
              </Text>
            </HStack>
          </Flex>
          <Text fontSize="sm" color="gray.600" noOfLines={1}>
            {preview}
          </Text>
          <HStack spacing={2} mt={2} flexWrap="wrap">
            {conversation.participant_ig_id ? (
              <Badge borderRadius="full" px={2.5} py={0.5} colorScheme="pink">
                @{conversation.participant_username || conversation.participant_ig_id}
              </Badge>
            ) : null}
            {conversation.ai_chat_responses ? (
              <Badge borderRadius="full" px={2.5} py={0.5} colorScheme="purple">
                AI on
              </Badge>
            ) : null}
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
};

export const InstagramThreadPlaceholder = ({
  title = 'Select a conversation',
  description = 'Choose a conversation from the inbox to reply to customers, review context, and keep Instagram work in one calm workspace.',
}: {
  title?: string;
  description?: string;
}) => (
  <Flex h="100%" align="center" justify="center" px={6}>
    <VStack spacing={4} textAlign="center">
      <Box
        w="76px"
        h="76px"
        borderRadius="24px"
        bg="white"
        border="1px solid"
        borderColor={igBorder}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 16px 40px rgba(15,23,32,0.06)"
        color={igAccent}
      >
        <Icon as={MessageCircleMore as ElementType} boxSize={9} />
      </Box>
      <VStack spacing={1}>
        <Text fontWeight="700" color="gray.900" fontSize="xl">
          {title}
        </Text>
        <Text fontSize="sm" color="gray.500" maxW="360px" lineHeight="1.7">
          {description}
        </Text>
      </VStack>
    </VStack>
  </Flex>
);

export const InstagramDateSeparator = ({ label }: { label: string }) => (
  <Flex justify="center" my={5}>
    <Text
      fontSize="xs"
      fontWeight="700"
      color="gray.500"
      px={3}
      py={1.5}
      borderRadius="999px"
      bg="rgba(255,255,255,0.82)"
      border="1px solid"
      borderColor={igBorder}
      backdropFilter="blur(8px)"
    >
      {label}
    </Text>
  </Flex>
);

export const InstagramMessageBubble = ({
  outgoing,
  children,
  meta,
  failed,
  attachments = [],
}: {
  outgoing?: boolean;
  children: React.ReactNode;
  meta?: React.ReactNode;
  failed?: boolean;
  attachments?: Array<Record<string, any>>;
}) => (
  <Box
    maxW={{ base: '85%', md: '74%' }}
    px={4}
    py={3}
    borderRadius="22px"
    borderBottomRightRadius={outgoing ? '8px' : '22px'}
    borderBottomLeftRadius={outgoing ? '22px' : '8px'}
    bg={outgoing ? '#C13584' : 'white'}
    color={outgoing ? 'white' : 'gray.900'}
    border="1px solid"
    borderColor={failed ? 'red.200' : outgoing ? 'transparent' : igBorder}
    boxShadow="0 10px 28px rgba(15,23,42,0.06)"
  >
    <VStack align="stretch" spacing={2}>
      {children ? (
        <Text fontSize="sm" whiteSpace="pre-wrap">
          {children}
        </Text>
      ) : null}
      {attachments.length ? (
        <VStack align="stretch" spacing={2}>
          {attachments.map((attachment, index) =>
            attachment.url ? (
              <Box
                key={`${attachment.url}-${index}`}
                as="a"
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                px={3}
                py={2}
                borderRadius="14px"
                bg={outgoing ? 'whiteAlpha.200' : '#f8fafc'}
                border="1px solid"
                borderColor={outgoing ? 'whiteAlpha.300' : igBorder}
              >
                <Text fontSize="sm" fontWeight="600">
                  {attachment.title || attachment.name || `${attachment.type || 'Attachment'} attachment`}
                </Text>
                <Text fontSize="xs" color={outgoing ? 'whiteAlpha.800' : 'gray.500'}>
                  Open attachment
                </Text>
              </Box>
            ) : (
              <Box
                key={`${attachment.name || attachment.type || index}`}
                px={3}
                py={2}
                borderRadius="14px"
                bg={outgoing ? 'whiteAlpha.200' : '#f8fafc'}
                border="1px solid"
                borderColor={outgoing ? 'whiteAlpha.300' : igBorder}
              >
                <Text fontSize="sm" fontWeight="600">
                  {attachment.name || `${attachment.type || 'Attachment'} attachment`}
                </Text>
              </Box>
            ),
          )}
        </VStack>
      ) : null}
      {meta ? (
        <Text
          fontSize="xs"
          color={outgoing ? 'whiteAlpha.800' : failed ? 'red.500' : 'gray.500'}
          textAlign="right"
        >
          {meta}
        </Text>
      ) : null}
    </VStack>
  </Box>
);

export const InstagramThreadHeader = ({
  title,
  subtitle,
  avatarName,
  avatarSrc,
  onBack,
  actions,
}: {
  title: string;
  subtitle?: string;
  avatarName?: string;
  avatarSrc?: string;
  onBack?: () => void;
  actions?: React.ReactNode;
}) => (
  <Flex
    px={{ base: 4, md: 6 }}
    py={4}
    align="center"
    justify="space-between"
    borderBottom="1px solid"
    borderColor={igBorder}
    bg="rgba(255,255,255,0.88)"
    backdropFilter="blur(14px)"
    position="sticky"
    top={0}
    zIndex={5}
  >
    <HStack spacing={3}>
      {onBack ? (
        <IconButton
          aria-label="Back to inbox"
          icon={<Icon as={ArrowLeft as ElementType} boxSize={4} />}
          variant="ghost"
          borderRadius="full"
          onClick={onBack}
          display={{ base: 'inline-flex', md: 'none' }}
        />
      ) : null}
      <Avatar
        size="md"
        name={avatarName || title}
        src={avatarSrc}
        bg={igAccent}
        color="white"
      />
      <VStack align="flex-start" spacing={0}>
        <Text fontWeight="700" color="gray.900">
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="sm" color="gray.500">
            {subtitle}
          </Text>
        ) : null}
      </VStack>
    </HStack>
    <HStack spacing={2}>{actions}</HStack>
  </Flex>
);

export const InstagramTopBarAction = ({
  icon,
  label,
  onClick,
}: {
  icon: ElementType;
  label: string;
  onClick?: () => void;
}) => (
  <IconButton
    aria-label={label}
    icon={<Icon as={icon} boxSize={4} />}
    variant="ghost"
    borderRadius="full"
    onClick={onClick}
  />
);

export const InstagramRefreshButton = ({
  onClick,
}: {
  onClick?: () => void;
}) => (
  <IconButton
    aria-label="Refresh"
    icon={<Icon as={RefreshCw as ElementType} boxSize={4} />}
    variant="ghost"
    borderRadius="full"
    onClick={onClick}
  />
);
